import type { FC } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useLang from '@/hooks/use-lang'
import useTranslate from '@/hooks/use-translate'
import type { Release } from '../types'
import {
  whatsNewFeatureBadgeSx,
  whatsNewFixBadgeSx,
  whatsNewReleaseBlockSx,
  whatsNewReleaseContentSx,
  whatsNewReleaseDateSx,
  whatsNewReleaseHeaderSx,
  whatsNewReleaseMetaSx,
  whatsNewReleaseSummarySx,
  whatsNewReleaseTitleSx,
  whatsNewSectionTitleSx,
} from '../whatsNewModalStyles'
import { ChangelogItem } from './ChangelogItem'
import { formatReleaseDate } from '../lib/format-release-date'

type ReleaseBlockProps = {
  release: Release
}

export const ReleaseBlock: FC<ReleaseBlockProps> = ({ release }) => {
  const { t } = useTranslate('whatsNew')
  const { lang } = useLang()
  const formattedDate = formatReleaseDate(release.date, lang)

  return (
    <Box sx={whatsNewReleaseBlockSx}>
      <Box sx={whatsNewReleaseHeaderSx}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '10px',
            padding: '8px 14px',
            minWidth: 88,
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 600,
              letterSpacing: '.8px',
              color: 'rgba(255,255,255,.7)',
              textTransform: 'uppercase',
              marginBottom: 0.25,
            }}
          >
            {t('versionPill')}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              letterSpacing: '-.01em',
              lineHeight: 1.1,
            }}
          >
            {release.version}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={whatsNewReleaseDateSx}>{formattedDate}</Typography>
          <Typography
            sx={{
              ...whatsNewReleaseTitleSx,
              marginBottom: release.summary ? 0.375 : 0,
            }}
          >
            {release.title}
          </Typography>
          {release.summary && (
            <Typography sx={whatsNewReleaseSummarySx}>{release.summary}</Typography>
          )}
        </Box>

        <Box sx={whatsNewReleaseMetaSx}>
          {release.features?.length > 0 && (
            <Typography component="span" sx={whatsNewFeatureBadgeSx}>
              {t('featureCount', { count: release.features.length })}
            </Typography>
          )}
          {release.fixes?.length > 0 && (
            <Typography component="span" sx={whatsNewFixBadgeSx}>
              {t('fixCount', { count: release.fixes.length })}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={whatsNewReleaseContentSx}>
        {release.features?.length > 0 && (
          <Box sx={{ marginBottom: release.fixes?.length > 0 ? 2.25 : 0 }}>
            <Box sx={whatsNewSectionTitleSx}>
              <span style={{ fontSize: 14 }}>🚀</span> {t('sectionFeatures')}
            </Box>
            <Stack gap={1}>
              {release.features.map((f) => (
                <ChangelogItem
                  key={`${release.releaseId}-${f.id}`}
                  item={f}
                  kind="feature"
                />
              ))}
            </Stack>
          </Box>
        )}

        {release.fixes?.length > 0 && (
          <Box>
            <Box sx={whatsNewSectionTitleSx}>
              <span style={{ fontSize: 14 }}>🐞</span> {t('sectionFixes')}
            </Box>
            <Stack gap={1}>
              {release.fixes.map((f) => (
                <ChangelogItem
                  key={`${release.releaseId}-${f.id}`}
                  item={f}
                  kind="fix"
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  )
}
